// =====================================================================
// PatientDetail (PME Sprint 1 / S50) — pagina /prontuario/paciente/:id.
// Hub do paciente com abas: Cadastro (editavel) + placeholders (Consultas /
// Documentos / Fotos) que entram nos Sprints 2-4. GET/PUT/DELETE /patients/:id.
// =====================================================================
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";

import PatientSummary from "./PatientSummary";
import PatientForm from "./PatientForm";
import EncounterList from "./EncounterList";
import DocumentList from "./DocumentList";
import EmittedDocuments from "./EmittedDocuments";
import SurgeryGallery from "./SurgeryGallery";
import PatientFinance from "./PatientFinance";
import ProtocolList from "./ProtocolList";
import PatientToBoardDialog from "../Quadros/PatientToBoardDialog";
import {
  getPatient,
  updatePatient,
  deletePatient,
  patientErrorMessage
} from "../../services/patientService";
import useIsMobile from "../../utils/useIsMobile";

const PatientDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const isMobile = useIsMobile();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const p = await getPatient(id);
        if (active) setPatient(p);
      } catch (err) {
        toast.error(patientErrorMessage(err));
        history.push("/prontuario");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, history]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const updated = await updatePatient(id, payload);
      setPatient(updated);
      toast.success("Paciente atualizado.");
    } catch (err) {
      toast.error(patientErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    setSaving(true);
    try {
      await deletePatient(id);
      toast.success("Paciente removido.");
      history.push("/prontuario");
    } catch (err) {
      toast.error(patientErrorMessage(err));
      setSaving(false);
    }
  };

  // Foto trocada/removida (PME Sprint 3a). Atualiza o paciente sem round-trip:
  // upload -> recebe o paciente atualizado; remoção -> limpa o photoUrl local.
  const handlePhotoChange = (updated) => {
    if (updated && updated.id) {
      setPatient(updated);
    } else {
      setPatient((prev) => (prev ? { ...prev, photoUrl: null } : prev));
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{patient ? patient.fullName : "Paciente"}</Title>
        <MainHeaderButtonsWrapper>
          <Button onClick={() => setBoardDialogOpen(true)} disabled={saving || !patient} color="primary">
            🗂️ Criar tarefa
          </Button>
          <Button onClick={() => history.push("/prontuario")} disabled={saving}>
            Voltar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper variant="outlined" style={{ marginBottom: 8 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
        >
          <Tab label="Resumo" />
          <Tab label="Cadastro" />
          <Tab label="Consultas" />
          <Tab label="Documentos" />
          <Tab label="Fotos" />
          <Tab label="Financeiro" />
          <Tab label="Protocolos" />
        </Tabs>
      </Paper>

      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <CircularProgress />
          </div>
        ) : tab === 0 && patient ? (
          <PatientSummary patient={patient} onPhotoChange={handlePhotoChange} onEditCadastro={() => setTab(1)} />
        ) : tab === 1 && patient ? (
          <PatientForm
            key={patient.id}
            mode="edit"
            initialPatient={patient}
            saving={saving}
            onSave={handleSave}
            onCancel={() => history.push("/prontuario")}
            onDelete={() => setConfirmDelete(true)}
            onPhotoChange={handlePhotoChange}
          />
        ) : tab === 2 && patient ? (
          <EncounterList patientId={patient.id} />
        ) : tab === 3 && patient ? (
          <div style={{ padding: 4 }}>
            <EmittedDocuments patientId={patient.id} patient={patient} />
            <div style={{ height: 28 }} />
            <DocumentList patientId={patient.id} />
          </div>
        ) : tab === 4 && patient ? (
          <div style={{ padding: 4 }}>
            <SurgeryGallery patientId={patient.id} />
          </div>
        ) : tab === 5 && patient ? (
          <div style={{ padding: 4 }}>
            <PatientFinance patientId={patient.id} />
          </div>
        ) : tab === 6 && patient ? (
          <div style={{ padding: 4 }}>
            <ProtocolList patientId={patient.id} />
          </div>
        ) : (
          <Typography variant="body2" color="textSecondary" style={{ padding: 24 }}>
            Disponível nos próximos sprints.
          </Typography>
        )}
      </div>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} fullScreen={isMobile}>
        <DialogTitle>Remover paciente</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover {patient ? patient.fullName : "este paciente"}? O
            prontuário fica arquivado (soft-delete) e pode ser recuperado depois.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancelar</Button>
          <Button onClick={handleDelete} style={{ color: "#d32f2f" }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
      <PatientToBoardDialog open={boardDialogOpen} onClose={() => setBoardDialogOpen(false)} patient={patient} />
    </MainContainer>
  );
};

export default PatientDetail;
